/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import TextView from '@airasia-phoenix/react-native/components/TextView';
import { pd } from '@airasia-phoenix/react-native/utils';
import { getColor } from '@airasia-phoenix/react-native/utils/utils';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Keyboard, Platform, Pressable, ScrollView } from 'react-native';
import { useTranslation } from 'react-i18next';
import styles from './styles';
import OTPView, { type OTPViewRef } from '../OTPView/OTPView';
import { AlternativeSignInOptionsView } from '../../components/AlternativeSignInOptionsView';
import { FullScreenLoaderOverlayView } from '../../components/FullScreenLoaderOverlayView';
import { useAutoRequestOtp } from '../../hooks/useAutoRequestOtp';
import { useVerifyEmailOtpFlow } from '../../hooks/otp/useVerifyEmailOtpFlow';
import { Purpose } from '../../managers/sso';
import { buildVerifyEmailViewProps } from './verifyEmailViewProps';
import { useVerifyEmailNavigationDefaults } from './verifyEmailNavigation';
import type { VerifyEmailViewProps } from './types';
import { useLogger } from '@airasia-rn/core-logger';
import { ACTIONS, EVENT_NAMES } from '../../config/analytics';
import type { OtpVerifiedResult } from '../../hooks/otp/types';
import {
  createAnalyticsLogger,
  getSsoOtpLabels,
} from '../../utils/loginSignupAnalyticsUtils';
import { resolveEmailOtpAnalytics } from '../../utils/verifyEmailOtpUtils';

export type { VerifyEmailViewProps } from './types';

export default function VerifyEmailView({
  purpose = Purpose.USERNAME_CHANGE,
  email,
  cancelDeletion = false,
  onVerified,
  onLoadingChange,
  testID,
}: VerifyEmailViewProps) {
  const { t } = useTranslation('account');
  const otpViewRef = useRef<OTPViewRef>(null);
  const { logEvent } = useLogger();
  const otpAnalytics = useMemo(
    () => resolveEmailOtpAnalytics(purpose),
    [purpose],
  );
  const otpLabels = useMemo(
    () => getSsoOtpLabels(otpAnalytics.verificationMethod, otpAnalytics.method),
    [otpAnalytics],
  );
  const logOtpAnalytics = useMemo(
    () =>
      createAnalyticsLogger(
        logEvent,
        otpAnalytics.screenName,
        otpAnalytics.category,
        otpAnalytics.enabled,
      ),
    [
      logEvent,
      otpAnalytics.category,
      otpAnalytics.enabled,
      otpAnalytics.screenName,
    ],
  );

  const startTimer = useCallback(() => otpViewRef.current?.startTimer(), []);

  const isPasswordlessAuth =
    purpose === Purpose.EMAIL_OTP_VERIFICATION_LOGIN ||
    purpose === Purpose.EMAIL_OTP_VERIFICATION_SIGNUP;

  const handleVerified = useCallback(
    async (result?: OtpVerifiedResult) => {
      // Activation event is logged by the verify-email screen.
      if (result?.signupActivation) return;

      // Success event is logged by useFinishSignIn; the session is forwarded
      // so sheet hosts get it in onLoginSuccess.
      await onVerified?.(result);
    },
    [onVerified],
  );

  const {
    submitOtp,
    requestOtp,
    errorMessage,
    errorCode,
    isLoading,
    clearError,
    resolvedEmail,
    behaviour,
  } = useVerifyEmailOtpFlow({
    purpose,
    email,
    cancelDeletion,
    startTimer,
    onVerified: isPasswordlessAuth ? handleVerified : onVerified,
  });

  useEffect(() => {
    logOtpAnalytics(ACTIONS.screenView, otpLabels.screen);
  }, [logOtpAnalytics, otpLabels.screen]);

  const handleSubmitOtp = useCallback(
    (otp: string) => {
      logOtpAnalytics(EVENT_NAMES.ssoOtpSubmitted, otpLabels.screen);
      submitOtp(otp);
    },
    [logOtpAnalytics, otpLabels.screen, submitOtp],
  );

  const handleRequestOtp = useCallback(() => {
    logOtpAnalytics(ACTIONS.tap, otpLabels.requestOTP);
    requestOtp();
  }, [logOtpAnalytics, otpLabels.requestOTP, requestOtp]);

  const handleLoginOtpOptionPress = useCallback(
    (label: string, onPress: () => void) => {
      if (otpAnalytics.isLogin) {
        logOtpAnalytics(ACTIONS.tap, otpLabels.option(label));
      }
      onPress();
    },
    [logOtpAnalytics, otpAnalytics.isLogin, otpLabels],
  );

  useAutoRequestOtp({ behaviour, requestOtp, startTimer });

  useEffect(() => {
    onLoadingChange?.(isLoading);
  }, [isLoading, onLoadingChange]);

  const viewProps = buildVerifyEmailViewProps({ resolvedEmail, testID }, t);
  const navigationDefaults = useVerifyEmailNavigationDefaults(email);

  return (
    <ScrollView
      style={styles.pageContainer}
      contentContainerStyle={styles.contentContainer}
      keyboardShouldPersistTaps="handled"
      keyboardDismissMode="on-drag"
      showsVerticalScrollIndicator={false}
    >
      <Pressable
        style={styles.container}
        onPress={Keyboard.dismiss}
        accessible={false}
      >
        <OTPView
          ref={otpViewRef}
          title={viewProps.title}
          showTitle={Platform.OS !== 'web'}
          subtitle={viewProps.subtitle}
          subtitleSpannableTextsProps={viewProps.subtitleSpannableTextsProps}
          errorMessage={errorMessage}
          errorCode={errorCode}
          onOTPSubmit={handleSubmitOtp}
          onRequestNewOTP={handleRequestOtp}
          onErrorClear={clearError}
          testID={testID}
          errorTestID={viewProps.testIDs.error}
          timerHeaderText={viewProps.timer.headerText}
          getTimerText={viewProps.timer.getTimerText}
          getTimerSpannableTextsProps={
            viewProps.timer.getTimerSpannableTextsProps
          }
          pinInputFieldTextStyle={styles.pinInputField}
          deferPinMount
        />
        {purpose === Purpose.EMAIL_OTP_VERIFICATION_LOGIN && (
          <AlternativeSignInOptionsView
            testID={testID}
            onSignInWithPassword={() =>
              handleLoginOtpOptionPress(
                'passwordLogin',
                navigationDefaults.onSignInWithPassword,
              )
            }
            onSignInWithPhoneNumber={() =>
              handleLoginOtpOptionPress(
                'otherLoginMethod',
                navigationDefaults.onSignInWithPhoneNumber,
              )
            }
            onSignInWithPasskey={() =>
              handleLoginOtpOptionPress(
                'passkey',
                navigationDefaults.onSignInWithPasskey,
              )
            }
          />
        )}
        {purpose === Purpose.EMAIL_OTP_VERIFICATION_SIGNUP && (
          <Pressable onPress={navigationDefaults.onSignUpWithDiffEmail}>
            <TextView
              style={styles.alternativeSignUp}
              text={viewProps.alternativeSignUpText}
            />
          </Pressable>
        )}
        <FullScreenLoaderOverlayView
          visible={isLoading && !onLoadingChange}
          backgroundColor={getColor(pd.sys.colour.overlay, 0)}
        />
      </Pressable>
    </ScrollView>
  );
}
